import { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Battery, TrendingUp, Wind, User, Zap, AlertTriangle } from 'lucide-react';
import './RangeCalculator.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RangeCalculator({ route }) {
  const { t } = useTranslation();
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    battery_kwh: 0.5,
    current_charge_percent: 100,
    route_distance_km: route?.distance_km || 30,
    elevation_gain_m: route?.elevation_gain_m || 200,
    wind_speed_kmh: 15,
    rider_weight_kg: 75,
    bike_weight_kg: 25,
    assistance_level: 2
  });

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate-range`, formData);
      setCalculation(response.data);
    } catch (error) {
      console.error('Error calculating range:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  return (
    <div className="range-calculator" data-testid="range-calculator">
      <h3 className="calculator-title">
        <Battery size={24} />
        {t('calculator.title')}
      </h3>

      <div className="calculator-form">
        <div className="form-row">
          <div className="form-group">
            <label>
              <Battery size={18} />
              {t('calculator.battery_capacity')}
            </label>
            <input
              type="number"
              step="0.1"
              value={formData.battery_kwh}
              onChange={(e) => handleInputChange('battery_kwh', e.target.value)}
              data-testid="battery-input"
            />
          </div>

          <div className="form-group">
            <label>
              <Zap size={18} />
              {t('calculator.current_charge')}
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.current_charge_percent}
              onChange={(e) => handleInputChange('current_charge_percent', e.target.value)}
              data-testid="charge-input"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              <TrendingUp size={18} />
              {t('calculator.elevation')}
            </label>
            <input
              type="number"
              value={formData.elevation_gain_m}
              onChange={(e) => handleInputChange('elevation_gain_m', e.target.value)}
              data-testid="elevation-input"
            />
          </div>

          <div className="form-group">
            <label>
              <Wind size={18} />
              {t('calculator.wind_speed')}
            </label>
            <input
              type="number"
              value={formData.wind_speed_kmh}
              onChange={(e) => handleInputChange('wind_speed_kmh', e.target.value)}
              data-testid="wind-input"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              <User size={18} />
              {t('calculator.rider_weight')}
            </label>
            <input
              type="number"
              value={formData.rider_weight_kg}
              onChange={(e) => handleInputChange('rider_weight_kg', e.target.value)}
              data-testid="weight-input"
            />
          </div>

          <div className="form-group">
            <label>
              <Zap size={18} />
              {t('calculator.assistance_level')}
            </label>
            <select
              value={formData.assistance_level}
              onChange={(e) => handleInputChange('assistance_level', e.target.value)}
              data-testid="assistance-input"
            >
              <option value="1">Eco (1)</option>
              <option value="2">Tour (2)</option>
              <option value="3">Sport (3)</option>
            </select>
          </div>
        </div>

        <button 
          className="calculate-btn"
          onClick={handleCalculate}
          disabled={loading}
          data-testid="calculate-btn"
        >
          {loading ? t('calculator.calculating') : t('calculator.calculate')}
        </button>
      </div>

      {calculation && (
        <div className="calculation-result" data-testid="calculation-result">
          <div className="result-header">
            <h4>{t('calculator.result_title')}</h4>
          </div>

          <div className="result-stats">
            <div className="result-stat">
              <span className="stat-label">{t('calculator.estimated_range')}</span>
              <span className="stat-value large">{calculation.estimated_range_km} km</span>
            </div>

            <div className="result-stat">
              <span className="stat-label">{t('calculator.can_complete')}</span>
              <span className={`stat-value ${calculation.can_complete_route ? 'success' : 'danger'}`}>
                {calculation.can_complete_route ? `✓ ${t('calculator.yes')}` : `✗ ${t('calculator.no')}`}
              </span>
            </div>

            <div className="result-stat">
              <span className="stat-label">{t('calculator.remaining_charge')}</span>
              <span className="stat-value">{calculation.remaining_charge_percent}%</span>
            </div>
          </div>

          {calculation.warning && (
            <div className="result-warning" data-testid="range-warning">
              <AlertTriangle size={20} />
              <span>{calculation.warning}</span>
            </div>
          )}

          {calculation.recommended_charging_stops.length > 0 && (
            <div className="result-recommendations">
              <h5>Empfohlene Ladestationen:</h5>
              <ul>
                {calculation.recommended_charging_stops.map((stop, idx) => (
                  <li key={idx}>{stop}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
